import addIcon from '@iconify/icons-material-symbols/add-rounded';
import deleteIcon from '@iconify/icons-material-symbols/delete-outline-rounded';
import { Icon } from '@iconify/react';
import { Box, Button, IconButton, List, ListItemButton, ListItemText, Typography } from '@mui/material';
import React from 'react';

import useLocales from 'hooks/useLocales';

import { ChatSession } from './chatSessionStore';

// ----------------------------------------------------------------------

interface ChatSessionListProps {
    sessions: ChatSession[];
    activeSessionId: string | null;
    // While a turn is in flight, starting or switching chats is disabled so a
    // reply can't be misfiled against the wrong session.
    busy: boolean;
    onSelect: (id: string) => void;
    onNew: () => void;
    onRequestDelete: (id: string) => void;
}

const ChatSessionList: React.FunctionComponent<ChatSessionListProps> = ({
    sessions,
    activeSessionId,
    busy,
    onSelect,
    onNew,
    onRequestDelete,
}) => {
    const { translate } = useLocales();
    const ordered = [...sessions].sort((a, b) => b.updatedAt - a.updatedAt);

    return (
        <Box sx={{ flexGrow: 1, minHeight: 0, overflowY: 'auto', px: 2, py: 2 }}>
            <Button
                fullWidth
                variant='outlined'
                startIcon={<Icon icon={addIcon} width={20} height={20} />}
                onClick={onNew}
                disabled={busy}
                sx={{ mb: 2 }}
            >
                {translate('chat_new_conversation')}
            </Button>

            {ordered.length === 0 ? (
                <Typography variant='body2' sx={{ color: 'text.secondary', textAlign: 'center', mt: 2 }}>
                    {translate('chat_empty_hint')}
                </Typography>
            ) : (
                <List disablePadding>
                    {ordered.map(session => (
                        <ListItemButton
                            key={session.id}
                            selected={session.id === activeSessionId}
                            disabled={busy}
                            onClick={() => onSelect(session.id)}
                            sx={{ borderRadius: 1, mb: 0.5, pr: 1 }}
                        >
                            <ListItemText
                                primary={session.title || translate('chat_untitled')}
                                primaryTypographyProps={{ variant: 'body2', noWrap: true }}
                            />
                            <IconButton
                                edge='end'
                                size='small'
                                aria-label={translate('chat_delete_confirm')}
                                onClick={event => {
                                    // Don't let the trash click also select the row.
                                    event.stopPropagation();
                                    onRequestDelete(session.id);
                                }}
                            >
                                <Icon icon={deleteIcon} width={20} height={20} />
                            </IconButton>
                        </ListItemButton>
                    ))}
                </List>
            )}
        </Box>
    );
};

export default ChatSessionList;
